'use client';

import React from 'react';
import { cn } from '@/utils/cn';
import { TypographyVariant } from '@/design-tokens/typography';

/**
 * Available heading levels (h1-h6)
 */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Available semantic variants that map to typography design tokens
 * Using the centralized TypographyVariant type for consistency
 */
export type HeadlineVariant = TypographyVariant;

/**
 * Available headline alignment options
 */
export type HeadlineAlign = 'inherit' | 'left' | 'center' | 'right' | 'justify';

/**
 * Available text colors
 */
export type HeadlineColor =
  | 'primary'
  | 'secondary'
  | 'brand'
  | 'error'
  | 'success'
  | 'warning'
  | 'neutral-gray-600'
  | 'neutral-gray-700'
  | 'neutral-gray-800'
  | 'neutral-black'
  | 'border-red'
  | 'border-green'
  | 'border-yellow';

/**
 * Props for the TextHeadline component
 */
export interface TextHeadlineProps {
  /** The heading level (h1-h6) for semantic HTML */
  level: HeadingLevel;
  /** Visual styling variant (can differ from semantic level) */
  variant?: HeadlineVariant;
  /** Text color */
  color?: HeadlineColor;
  /** Text alignment */
  align?: HeadlineAlign;
  /** Additional CSS classes */
  className?: string;
  /** Child content */
  children?: React.ReactNode;
  /** HTML id attribute */
  id?: string;
  /** Optional state to indicate content is being loaded */
  isLoading?: boolean;
  /** Additional CSS classes to apply when loading */
  loadingClassName?: string;
  /** Optional text string that renders as children */
  text?: string;
  /** ARIA label for accessibility */
  'aria-label'?: string;
  /** ARIA describedby for accessibility */
  'aria-describedby'?: string;
  /** HTML data-content attribute */
  'data-content'?: string;
  /** HTML data-testid attribute */
  'data-testid'?: string;
  /** Number of loading lines to display */
  loadingLines?: number;
}

/**
 * Mapping of variants to their font family, size, weight, and line height
 */
const variantStyles: Record<HeadlineVariant, string> = {
  pageTitle: 'font-display text-page-title font-bold leading-page-title',
  sectionTitle: 'font-display text-section-title font-bold leading-section-title',
  sectionSubTitle: 'font-display text-section-subtitle font-bold leading-section-subtitle',
  cardTitle: 'font-text text-card-title font-bold leading-card-title',
  bodyTitle: 'font-text text-body-title font-bold leading-body-title',
  bodyLarge: 'font-text text-body-large font-regular leading-body-large',
  bodyMedium: 'font-text text-body font-regular leading-body',
  bodySmall: 'font-text text-body-small font-regular leading-body-small',
  tag: 'font-text text-tag font-medium leading-tag',
  label: 'font-text text-label font-medium leading-label',
  buttonSmall: 'font-text text-button-small font-medium leading-button-small',
  buttonLarge: 'font-text text-button-large font-medium leading-button-large',
  button: 'font-text text-button font-medium leading-button',
};

/**
 * Mapping of colors to their corresponding CSS classes
 */
const colorStyles: Record<HeadlineColor, string> = {
  primary: 'text-semantic-text-primary',
  secondary: 'text-semantic-text-secondary',
  brand: 'text-brand-primary',
  error: 'text-system-error',
  success: 'text-system-success',
  warning: 'text-system-warning',
  'neutral-gray-600': 'text-neutral-gray-600',
  'neutral-gray-700': 'text-neutral-gray-700',
  'neutral-gray-800': 'text-neutral-gray-800',
  'neutral-black': 'text-neutral-black',
  'border-red': 'text-accent-border-red',
  'border-green': 'text-accent-border-green',
  'border-yellow': 'text-accent-border-yellow',
};

/**
 * Mapping of text alignment options to their corresponding CSS classes
 */
const alignStyles: Record<HeadlineAlign, string> = {
  inherit: 'text-[inherit]',
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

/**
 * Default variants for each heading level
 */
const defaultVariants: Record<HeadingLevel, HeadlineVariant> = {
  1: 'pageTitle',
  2: 'sectionTitle',
  3: 'sectionSubTitle',
  4: 'cardTitle',
  5: 'bodyTitle',
  6: 'bodyMedium',
};

/**
 * Default height variants for loading state based on heading level
 */
const loadingLevelHeights: Record<HeadingLevel, string> = {
  1: 'h-16',
  2: 'h-14',
  3: 'h-12',
  4: 'h-10',
  5: 'h-8',
  6: 'h-6',
};

/**
 * TextHeadline Component
 *
 * A flexible heading component that provides semantic HTML structure (h1-h6)
 * while allowing independent visual styling through variants.
 *
 * Features:
 * - Semantic HTML heading levels (h1-h6)
 * - Visual variants independent of semantic level
 * - Design system typography integration
 * - Accessible by default
 * - Consistent color system
 *
 * @example
 * ```tsx
 * <TextHeadline level={1} variant="pageTitle" color="primary">
 *   Welcome to the App
 * </TextHeadline>
 *
 * <TextHeadline level={2} variant="sectionTitle">
 *   Section Title
 * </TextHeadline>
 *
 * <TextHeadline level={3} variant="cardTitle" color="secondary">
 *   Card Title
 * </TextHeadline>
 * ```
 */
export const TextHeadline: React.FC<TextHeadlineProps> = ({
  level = 1,
  variant,
  color = 'primary',
  align = 'inherit',
  className,
  children,
  id,
  isLoading = false,
  loadingClassName,
  loadingLines = 1,
  text,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'data-content': dataContent,
  'data-testid': testId,
  ...rest
}) => {
  // Return null if no children (or text) provided
  if (!isLoading && !children && !text) {
    return null;
  }

  // Use provided variant or default based on heading level
  const effectiveVariant = variant || defaultVariants[level];

  // Manually combine classes to avoid tailwind-merge conflicts
  // between text-* (size) and text-* (color)
  const baseClasses = variantStyles[effectiveVariant];
  const colorClass = colorStyles[color];
  const alignClass = alignStyles[align];

  // Combine base classes with color and additional classes
  // We avoid using cn() for the base + color combination to prevent text-* conflicts
  const classes = [baseClasses, colorClass, alignClass, className].filter(Boolean).join(' ');

  // Create the appropriate heading element
  const HeadingElement = `h${level}` as const;

  // Get the classes for the loading state
  const loadingClasses = cn(
    'animate-pulse bg-neutral-gray-300 w-96',
    loadingLevelHeights[level],
    {
      'h-16': variant === 'pageTitle',
      'h-8': variant === 'sectionTitle',
    },
    loadingClassName,
  );

  // Return the element
  return (
    <>
      {isLoading ? (
        Array.from({ length: loadingLines }).map((_, index) => (
          <div key={index} className={loadingClasses} />
        ))
      ) : (
        <HeadingElement
          className={classes}
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          data-content={dataContent}
          data-testid={testId}
          {...rest}
        >
          {text || children}
        </HeadingElement>
      )}
    </>
  );
};

/**
 * Pre-configured heading components for common use cases
 */
export const TextHeadlinePageTitle = (props: Omit<TextHeadlineProps, 'level' | 'variant'>) => (
  <TextHeadline level={1} variant="pageTitle" {...props} />
);

export const TextHeadlineSectionTitle = (props: Omit<TextHeadlineProps, 'level' | 'variant'>) => (
  <TextHeadline level={2} variant="sectionTitle" {...props} />
);

export const TextHeadlineSectionSubTitle = (
  props: Omit<TextHeadlineProps, 'level' | 'variant'>,
) => <TextHeadline level={3} variant="sectionSubTitle" {...props} />;

export const TextHeadlineCardTitle = (props: Omit<TextHeadlineProps, 'level' | 'variant'>) => (
  <TextHeadline level={4} variant="cardTitle" {...props} />
);

export const TextHeadlineBodyTitle = (props: Omit<TextHeadlineProps, 'level' | 'variant'>) => (
  <TextHeadline level={5} variant="bodyTitle" {...props} />
);

// Export the main component as default
TextHeadline.displayName = 'TextHeadline';

export default TextHeadline;
